import { useCallback, useEffect, useMemo, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { ICalculatorEngine } from '@src/types';
import { useCalculatorStore } from '@src/stores';

import { MAX_NUMBER_LENGTH, OPERATORS } from '../../constants';
import { closeAllParentheses } from '../../utils';
import mathInstance from '../../utils/mathInstance';

export const useCalculatorEngine = (): ICalculatorEngine => {
  const [input, setInput] = useState<string>('0');
  const [result, setResult] = useState<string | null>(null);
  const [expression, setExpression] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [justCalculated, setJustCalculated] = useState<boolean>(false);

  const display = result ?? input;
  const operationText = expression || null;

  const { setCalculatorState, calculatorState, selectedCalculatorMode } = useCalculatorStore(
    useShallow((state) => ({
      setCalculatorState: state.setCalculatorState,
      calculatorState: state.calculatorState,
      selectedCalculatorMode: state.selectedCalculatorMode,
    }))
  );

  const getCurrentNumber = useCallback((expr: string): string => {
    if (!expr) return '';

    const lastPlus = expr.lastIndexOf('+');
    const lastMult = expr.lastIndexOf('×');
    const lastDiv = expr.lastIndexOf('÷');
    const rawMinusIndex = expr.lastIndexOf('-');

    let lastMinus = -1;
    if (rawMinusIndex > 0) {
      const prevChar = expr[rawMinusIndex - 1];
      if (!['+', '-', '×', '÷', '('].includes(prevChar)) {
        lastMinus = rawMinusIndex;
      }
    }

    const lastOpIndex = Math.max(lastPlus, lastMinus, lastMult, lastDiv);

    return expr.slice(lastOpIndex + 1);
  }, []);

  const appendDigit = useCallback(
    (digit?: string) => {
      if (error) return;
      if (!digit) return;
      setError(null);

      if (justCalculated) {
        setInput(digit);
        setExpression('');
        setResult(null);
        setJustCalculated(false);
        return;
      }

      const currentNumber = getCurrentNumber(input);

      if (currentNumber.length >= MAX_NUMBER_LENGTH) {
        return;
      }

      const lastChar = input.slice(-1);
      if (lastChar === ')' || lastChar === '!' || input.endsWith('pi')) {
        setInput(input + '×' + digit);
        return;
      }

      if (input === '0' && digit !== '0') {
        setInput(digit);
      } else if (input === '0' && digit === '0') {
        return;
      } else {
        setInput(input + digit);
      }
    },
    [error, getCurrentNumber, input, justCalculated]
  );

  const appendDecimal = useCallback(() => {
    if (error) return;
    setError(null);

    if (justCalculated) {
      setInput('0.');
      setExpression('');
      setResult(null);
      setJustCalculated(false);
      return;
    }

    const currentNumber = getCurrentNumber(input);

    if (currentNumber.length >= MAX_NUMBER_LENGTH) {
      return;
    }

    if (!currentNumber.includes('.')) {
      setInput(input + '.');
    }
  }, [error, getCurrentNumber, input, justCalculated]);

  const applyOperator = useCallback(
    (op?: string) => {
      if (error) return;
      if (!op) return;
      setError(null);
      setResult(null);
      setJustCalculated(false);

      const lastChar = input.slice(-1);

      if (OPERATORS.includes(lastChar)) {
        setInput(input.slice(0, -1) + op);
        return;
      }

      if (input === '0' && op === '-') {
        setInput('-');
        return;
      }

      setInput(input + op);
    },
    [error, input]
  );

  const evaluateExpression = useCallback(() => {
    if (error) return;
    if (!input || input === '0') return;

    try {
      const closedExpression = closeAllParentheses(input);
      let mathExpression = closedExpression.replace(/×/g, '*').replace(/÷/g, '/');

      // Handle percentages "realistically" (Business Logic)
      // Standard mathjs treats "50+10%" as "50+0.1".
      // We want "50+10%" to be "50 + (10% of 50)" -> "55".
      // Strategy: Replace " [+|-] N %" with " * (1 [+|-] N/100) "
      // This works well for chained addition/subtraction: 50 + 10% + 5% -> 50 * 1.1 * 1.05 = 57.75

      // Regex finding: [operator or start] [number] [operator +-] [number] %
      // However, simplified greedy replacement on " + number %" works for most basic sequences.

      // Replace (+ N%) with (* (1 + N/100))
      mathExpression = mathExpression.replace(/\+\s*(\d+(?:\.\d+)?)\s*%/g, '* (1 + $1/100)');

      // Replace (- N%) with (* (1 - N/100))
      mathExpression = mathExpression.replace(/-\s*(\d+(?:\.\d+)?)\s*%/g, '* (1 - $1/100)');

      // Replace remaining % (e.g. in * or /) with /100
      mathExpression = mathExpression.replace(/%/g, '/100');

      if (/\/\s*\(?\s*0+(\.0*)?\s*\)?(?![0-9.])/.test(mathExpression)) {
        setError('Undefined');
        setExpression(closedExpression);
        setInput('0');
        setResult(null);
        setJustCalculated(true);
        return;
      }

      const rawResult = mathInstance.evaluate(mathExpression);
      const numResult = rawResult as number;

      if (!Number.isFinite(numResult) || Number.isNaN(numResult)) {
        setError('Math Error');
        setExpression(closedExpression);
        setInput('0');
        setResult(null);
        setJustCalculated(true);
        return;
      }

      let formattedResult: string;
      if (Number.isInteger(numResult)) {
        formattedResult = String(numResult);
      } else if (Math.abs(numResult) < 1e-10 || Math.abs(numResult) > 1e10) {
        formattedResult = numResult.toExponential(10).replace(/\.?0+e/, 'e');
      } else {
        formattedResult = numResult.toPrecision(15).replace(/\.?0+$/, '');
      }

      setExpression(closedExpression);
      setResult(formattedResult);
      setInput(formattedResult);
      setJustCalculated(true);
    } catch (e) {
      setError('Math Error');
      setExpression(closeAllParentheses(input));
      setInput('0');
      setResult(null);
      setJustCalculated(true);
    }
  }, [error, input]);

  const applyPercent = useCallback(() => {
    if (error) return;
    setError(null);
    setResult(null);
    setJustCalculated(false);

    if (!input || input === '0') return;

    if (input.endsWith('%')) return;

    const currentNumber = getCurrentNumber(input);

    // If we can't get a valid number (e.g. input is just "-"), set error
    if (!currentNumber) {
      setError('Undefined');
      return;
    }

    const value = parseFloat(currentNumber);
    // If parsing fails, set error
    if (isNaN(value)) {
      setError('Undefined');
      return;
    }

    setInput(input + '%');
  }, [error, input, getCurrentNumber]);

  const clearAll = useCallback(() => {
    setInput('0');
    setExpression('');
    setError(null);
    setResult(null);
    setJustCalculated(false);
  }, []);

  const backspace = useCallback(() => {
    if (error) return;
    setError(null);

    if (justCalculated) {
      setInput('0');
      setExpression('');
      setResult(null);
      setJustCalculated(false);
      return;
    }

    if (input.length <= 1) {
      setInput('0');
      return;
    }

    setInput(input.slice(0, -1));
  }, [error, input, justCalculated]);

  const insertToken = useCallback(
    (token?: string) => {
      if (error) return;
      if (!token) return;
      setError(null);

      if (justCalculated) {
        setInput(token);
        setExpression('');
        setResult(null);
        setJustCalculated(false);
        return;
      }

      setResult(null);
      setJustCalculated(false);

      if (input === '0') {
        setInput(token);
        return;
      }

      setInput(input + token);
    },
    [error, input, justCalculated]
  );

  const updateInput = useCallback(
    (newInput: string) => {
      if (error) return;
      setInput(newInput);
      setError(null);
      setResult(null);
      setJustCalculated(false);
    },
    [error]
  );

  const engineState = useMemo(
    () => ({
      display,
      expression,
      operationText,
      result,
      error,
      input,
    }),
    [display, error, expression, operationText, result, input]
  );

  const hydrateSharedState = useCallback((sharedExpression: string, sharedResult: string) => {
    const nextExpression = sharedExpression ?? '';
    const nextResult = sharedResult ?? '';
    const nextInput = nextResult || nextExpression || '0';

    setExpression(nextExpression);
    setResult(nextResult ? nextResult : null);
    setInput(nextInput);
    setError(null);
    setJustCalculated(Boolean(nextResult));
  }, []);

  const actions = useMemo(
    () => ({
      appendDigit,
      appendDecimal,
      applyOperator,
      evaluateExpression,
      applyPercent,
      clearAll,
      backspace,
      insertToken,
      updateInput,
      hydrateSharedState,
    }),
    [
      appendDecimal,
      appendDigit,
      applyOperator,
      applyPercent,
      backspace,
      clearAll,
      evaluateExpression,
      insertToken,
      hydrateSharedState,
      updateInput,
    ]
  );

  useEffect(() => {
    // Preventing engine from updating store if we are in converter mode
    if (selectedCalculatorMode === 'converter') return;

    // Don't overwrite a meaningful result with '0' when switching modes
    // This preserves results when switching between basic and scientific modes
    if (display === '0' && calculatorState?.result && calculatorState?.result !== '0') {
      return;
    }

    setCalculatorState({
      error,
      result: display,
      expression: operationText,
    });
  }, [error, operationText, display, setCalculatorState, selectedCalculatorMode, calculatorState?.result]);

  return {
    state: engineState,
    actions,
    meta: {
      justCalculated,
    },
  };
};

export default useCalculatorEngine;
